import { useRef, useState } from 'react'
import { Button } from '@arcanecircle/ui/grimoire/button'
import { Input } from '@arcanecircle/ui/ui/input'
import { Switch } from '@arcanecircle/ui/ui/switch'
import { Slider } from '@arcanecircle/ui/ui/slider'
import type { MapConfig } from '@/types'
import { useVTTStore } from '@/store'
import { socketService } from '@/services/socket'

interface MapConfigPanelProps {
  map: MapConfig
  onUpdate: (mapId: string, updates: Partial<MapConfig>) => void
  /** GM-only map deletion. When absent, the delete control is hidden. */
  onDelete?: (mapId: string) => void | Promise<void>
}

/**
 * v5 map configuration — read-mode shows a stat grid + edit pill;
 * edit mode swaps to a flat panel of Grimoire form primitives.
 */
export function MapConfigPanel({ map, onUpdate, onDelete }: MapConfigPanelProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [editedName, setEditedName] = useState(map.name)
  const [editedWidth, setEditedWidth] = useState(map.width)
  const [editedHeight, setEditedHeight] = useState(map.height)
  const [editedGridSize, setEditedGridSize] = useState(map.gridSize)
  const [editedGridColor, setEditedGridColor] = useState(map.gridColor)
  const [editedGridOpacity, setEditedGridOpacity] = useState(map.gridOpacity)
  const [editedShowGrid, setEditedShowGrid] = useState(map.showGrid)
  const [editedGridOffsetX, setEditedGridOffsetX] = useState(map.gridOffsetX)
  const [editedGridOffsetY, setEditedGridOffsetY] = useState(map.gridOffsetY)
  const [editedBgOffsetX, setEditedBgOffsetX] = useState(map.backgroundOffsetX)
  const [editedBgOffsetY, setEditedBgOffsetY] = useState(map.backgroundOffsetY)
  const [editedBgScaleX, setEditedBgScaleX] = useState(map.backgroundScaleX)
  const [editedBgScaleY, setEditedBgScaleY] = useState(map.backgroundScaleY)
  const [editedBgFit, setEditedBgFit] = useState(map.backgroundFit ?? 'stretch')
  const [newImageFile, setNewImageFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const updateMap = useVTTStore((s) => s.updateMap)
  const originalBgRef = useRef({
    backgroundOffsetX: map.backgroundOffsetX,
    backgroundOffsetY: map.backgroundOffsetY,
    backgroundScaleX: map.backgroundScaleX,
    backgroundScaleY: map.backgroundScaleY,
    backgroundFit: map.backgroundFit ?? 'stretch',
  })

  const enterEdit = () => {
    originalBgRef.current = {
      backgroundOffsetX: map.backgroundOffsetX,
      backgroundOffsetY: map.backgroundOffsetY,
      backgroundScaleX: map.backgroundScaleX,
      backgroundScaleY: map.backgroundScaleY,
      backgroundFit: map.backgroundFit ?? 'stretch',
    }
    setEditedBgOffsetX(map.backgroundOffsetX)
    setEditedBgOffsetY(map.backgroundOffsetY)
    setEditedBgScaleX(map.backgroundScaleX)
    setEditedBgScaleY(map.backgroundScaleY)
    setEditedBgFit(map.backgroundFit ?? 'stretch')
    setIsEditing(true)
  }

  const previewBg = (patch: Partial<MapConfig>) => {
    updateMap(map.id, patch)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file')
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('Image must be smaller than 10MB')
      return
    }
    setNewImageFile(file)
    setUploadError(null)
  }

  const handleSave = async () => {
    // Validate dimensions before doing any work — mirror MapCreationModal
    // bounds (width/height 5–100 squares, gridSize 20–100 px). Guards against
    // NaN/empty so an empty input never persists a broken map.
    if (!Number.isFinite(editedWidth) || editedWidth < 5 || editedWidth > 100) {
      setUploadError('Width must be between 5 and 100 squares')
      return
    }
    if (!Number.isFinite(editedHeight) || editedHeight < 5 || editedHeight > 100) {
      setUploadError('Height must be between 5 and 100 squares')
      return
    }
    if (
      !Number.isFinite(editedGridSize) ||
      editedGridSize < 20 ||
      editedGridSize > 100
    ) {
      setUploadError('Grid size must be between 20 and 100 px')
      return
    }

    setIsUploading(true)
    setUploadError(null)
    try {
      const updates: Partial<MapConfig> = {
        name: editedName,
        width: editedWidth,
        height: editedHeight,
        gridSize: editedGridSize,
        gridColor: editedGridColor,
        gridOpacity: editedGridOpacity,
        showGrid: editedShowGrid,
        gridOffsetX: editedGridOffsetX,
        gridOffsetY: editedGridOffsetY,
        backgroundOffsetX: editedBgOffsetX,
        backgroundOffsetY: editedBgOffsetY,
        backgroundScaleX: editedBgScaleX,
        backgroundScaleY: editedBgScaleY,
        backgroundFit: editedBgFit,
      }

      if (newImageFile) {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001'
        const formData = new FormData()
        formData.append('image', newImageFile)
        // Server binds the upload to the JWT's session — no need to
        // pass sessionId as a query param. Auth header is required;
        // missing it returns 401 "token required".
        const token = socketService.getTokenFromStorage()
        const uploadResponse = await fetch(`${apiUrl}/api/maps/upload`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          body: formData,
        })
        if (!uploadResponse.ok) {
          const errorData = await uploadResponse
            .json()
            .catch(() => ({ error: 'Unknown error' }))
          throw new Error(
            errorData.error || `Failed to upload image (${uploadResponse.status})`,
          )
        }
        const uploadResult = await uploadResponse.json()
        updates.backgroundUrl = uploadResult.url
      }

      onUpdate(map.id, updates)
      setIsEditing(false)
      setNewImageFile(null)
    } catch (error) {
      setUploadError(
        error instanceof Error ? error.message : 'Failed to save changes',
      )
    } finally {
      setIsUploading(false)
    }
  }

  const handleCancel = () => {
    setEditedName(map.name)
    setEditedWidth(map.width)
    setEditedHeight(map.height)
    setEditedGridSize(map.gridSize)
    setEditedGridColor(map.gridColor)
    setEditedGridOpacity(map.gridOpacity)
    setEditedShowGrid(map.showGrid)
    setEditedGridOffsetX(map.gridOffsetX)
    setEditedGridOffsetY(map.gridOffsetY)
    updateMap(map.id, originalBgRef.current)
    setEditedBgOffsetX(originalBgRef.current.backgroundOffsetX)
    setEditedBgOffsetY(originalBgRef.current.backgroundOffsetY)
    setEditedBgScaleX(originalBgRef.current.backgroundScaleX)
    setEditedBgScaleY(originalBgRef.current.backgroundScaleY)
    setEditedBgFit(originalBgRef.current.backgroundFit)
    setNewImageFile(null)
    setUploadError(null)
    setConfirmingDelete(false)
    setIsEditing(false)
  }

  const handleDelete = async () => {
    if (!onDelete) return
    setIsDeleting(true)
    try {
      // onDelete (App.handleMapDelete) swallows its own errors and alerts,
      // and the panel unmounts once the active map leaves the store, so we
      // don't reset state on the success path. Reset only if it rejects.
      await onDelete(map.id)
    } catch {
      setIsDeleting(false)
      setConfirmingDelete(false)
    }
  }

  if (!isEditing) {
    return (
      <section className="ac-panel">
        <div className="ac-panel-header">
          <div>
            <div className="ac-panel-eyebrow">Active map</div>
            <div className="ac-panel-title">{map.name}</div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={enterEdit}
          >
            Edit
          </Button>
        </div>

        <div className="vtt-stat-grid">
          <div className="vtt-stat">
            <div className="k">Size</div>
            <div className="v" style={{ fontSize: '0.875rem' }}>
              {map.width}×{map.height}
            </div>
          </div>
          <div className="vtt-stat">
            <div className="k">Grid</div>
            <div className="v" style={{ fontSize: '0.875rem' }}>
              {map.gridSize}
              <small>px</small>
            </div>
          </div>
          <div className="vtt-stat">
            <div className="k">Vision</div>
            <div className="v" style={{ fontSize: '0.6875rem' }}>
              {map.losEnabled ? 'LoS + Fog' : 'Fog'}
            </div>
          </div>
        </div>

        <div className="ac-help">
          {map.showGrid ? `Grid ${(map.gridOpacity * 100).toFixed(0)}% · ` : 'Grid off · '}
          {map.backgroundUrl ? 'Background set' : 'No background'}
        </div>
      </section>
    )
  }

  return (
    <section className="ac-panel">
      <div className="ac-panel-header">
        <div>
          <div className="ac-panel-eyebrow">Editing map</div>
          <div className="ac-panel-title">{map.name}</div>
        </div>
      </div>

      {uploadError && (
        <div
          style={{
            padding: '0.625rem 0.75rem',
            background: 'hsl(var(--ac-danger) / 0.15)',
            border: '1px solid hsl(var(--ac-danger) / 0.4)',
            color: 'hsl(var(--ac-danger))',
            borderRadius: 'var(--ac-radius)',
            fontSize: '0.75rem',
          }}
        >
          {uploadError}
        </div>
      )}

      <div>
        <label className="ac-label">Map name</label>
        <Input
          type="text"
          value={editedName}
          onChange={(e) => setEditedName(e.target.value)}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
        <div>
          <label className="ac-label">Width (squares)</label>
          <Input
            type="number"
            value={editedWidth}
            min={5}
            max={100}
            onChange={(e) => setEditedWidth(parseInt(e.target.value) || 0)}
          />
        </div>
        <div>
          <label className="ac-label">Height (squares)</label>
          <Input
            type="number"
            value={editedHeight}
            min={5}
            max={100}
            onChange={(e) => setEditedHeight(parseInt(e.target.value) || 0)}
          />
        </div>
      </div>

      <div>
        <label className="ac-label">Grid size (px per square)</label>
        <Input
          type="number"
          value={editedGridSize}
          min={20}
          max={100}
          onChange={(e) => setEditedGridSize(parseInt(e.target.value) || 0)}
        />
      </div>

      <div>
        <label className="ac-label">Background image</label>
        {map.backgroundUrl && !newImageFile && (
          <div
            className="ac-help"
            style={{ marginBottom: '0.375rem', wordBreak: 'break-all' }}
          >
            Current: {map.backgroundUrl.split('/').pop()}
          </div>
        )}
        <Input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ padding: '0.375rem 0.75rem', height: 'auto' }}
        />
        {newImageFile && (
          <div
            className="ac-help"
            style={{ marginTop: '0.375rem', color: 'hsl(var(--ac-success))' }}
          >
            New: {newImageFile.name} ({(newImageFile.size / 1024 / 1024).toFixed(2)} MB)
          </div>
        )}
      </div>

      {map.backgroundUrl && (
        <>
          <Switch
            checked={editedBgFit === 'stretch'}
            onCheckedChange={(on) => {
              const fit = on ? 'stretch' : 'contain'
              setEditedBgFit(fit)
              previewBg({ backgroundFit: fit })
            }}
          >
            Stretch to fill
          </Switch>
          <div className="ac-help" style={{ marginTop: '-0.25rem' }}>
            {editedBgFit === 'stretch'
              ? 'Image fills the grid, distorting its aspect ratio if needed.'
              : 'Image fits the grid preserving its aspect ratio (letterboxed).'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div>
              <label className="ac-label">Bg offset X</label>
              <Input type="number" value={editedBgOffsetX}
                onChange={(e) => { const v = parseInt(e.target.value) || 0; setEditedBgOffsetX(v); previewBg({ backgroundOffsetX: v }) }} />
            </div>
            <div>
              <label className="ac-label">Bg offset Y</label>
              <Input type="number" value={editedBgOffsetY}
                onChange={(e) => { const v = parseInt(e.target.value) || 0; setEditedBgOffsetY(v); previewBg({ backgroundOffsetY: v }) }} />
            </div>
          </div>
          <div>
            <label className="ac-label">Bg scale X · {(editedBgScaleX * 100).toFixed(0)}%</label>
            <Slider
              min={0.1}
              max={4}
              step={0.05}
              value={[editedBgScaleX]}
              onValueChange={([v]) => { setEditedBgScaleX(v); previewBg({ backgroundScaleX: v }) }}
            />
          </div>
          <div>
            <label className="ac-label">Bg scale Y · {(editedBgScaleY * 100).toFixed(0)}%</label>
            <Slider
              min={0.1}
              max={4}
              step={0.05}
              value={[editedBgScaleY]}
              onValueChange={([v]) => { setEditedBgScaleY(v); previewBg({ backgroundScaleY: v }) }}
            />
          </div>
        </>
      )}

      <Switch checked={editedShowGrid} onCheckedChange={setEditedShowGrid}>
        Show grid
      </Switch>

      {editedShowGrid && (
        <>
          <div>
            <label className="ac-label">Grid color</label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="color"
                value={editedGridColor}
                onChange={(e) => setEditedGridColor(e.target.value)}
                style={{
                  width: 44,
                  height: 40,
                  padding: 2,
                  borderRadius: 'var(--ac-radius)',
                  border: '1px solid hsl(var(--ac-dark-accent))',
                  background: 'hsl(var(--ac-dark-primary) / 0.6)',
                  cursor: 'pointer',
                }}
              />
              <Input
                type="text"
                value={editedGridColor}
                onChange={(e) => setEditedGridColor(e.target.value)}
                style={{ flex: 1 }}
              />
            </div>
          </div>

          <div>
            <label className="ac-label">
              Grid opacity · {(editedGridOpacity * 100).toFixed(0)}%
            </label>
            <Slider
              min={0}
              max={1}
              step={0.05}
              value={[editedGridOpacity]}
              onValueChange={([v]) => setEditedGridOpacity(v)}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div>
              <label className="ac-label">Offset X</label>
              <Input
                type="number"
                value={editedGridOffsetX}
                onChange={(e) =>
                  setEditedGridOffsetX(parseInt(e.target.value) || 0)
                }
              />
            </div>
            <div>
              <label className="ac-label">Offset Y</label>
              <Input
                type="number"
                value={editedGridOffsetY}
                onChange={(e) =>
                  setEditedGridOffsetY(parseInt(e.target.value) || 0)
                }
              />
            </div>
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleCancel}
          disabled={isUploading}
          style={{ flex: 1 }}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={isUploading}
          style={{ flex: 1 }}
        >
          {isUploading ? 'Saving…' : 'Save'}
        </Button>
      </div>

      {/* Danger zone — GM-only map deletion, gated behind an inline
          confirm so a single misclick can't wipe a map (+ its tokens,
          fog, and barriers, which cascade server-side). */}
      {onDelete && (
        <div
          style={{
            marginTop: '0.25rem',
            paddingTop: '0.75rem',
            borderTop: '1px solid hsl(var(--ac-dark-accent))',
          }}
        >
          {!confirmingDelete ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setConfirmingDelete(true)}
              disabled={isUploading || isDeleting}
              style={{
                width: '100%',
                color: 'hsl(var(--ac-danger))',
                borderColor: 'hsl(var(--ac-danger) / 0.5)',
              }}
            >
              Delete map
            </Button>
          ) : (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
            >
              <div
                className="ac-help"
                style={{ color: 'hsl(var(--ac-danger))' }}
              >
                Delete “{map.name}”? Its tokens, fog, and barriers go with it,
                for everyone. This can’t be undone.
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={isDeleting}
                  style={{ flex: 1 }}
                >
                  Keep
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  style={{
                    flex: 1,
                    background: 'hsl(var(--ac-danger))',
                    borderColor: 'hsl(var(--ac-danger))',
                  }}
                >
                  {isDeleting ? 'Deleting…' : 'Delete map'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
